import { FC, useEffect, useState, useCallback } from 'react'
import { ArrowLeft, FolderOpen, FileIcon, Loader2 } from 'lucide-react'
import { Button } from '../ui/button'
import { useProjects } from '../../hooks/useProjects'
import { useChat } from '../../contexts/ChatContext'
import api, { listProjectFiles } from '../../services/api'
import { cn } from '../../lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { ProjectFilesPanel } from './ProjectFilesPanel'
import { ProjectChatInput } from './ProjectChatInput'

interface ChatPreview {
  id: string
  title: string
  lastMessage?: string
  updatedAt: string
}

interface ProjectDetailViewProps {
  projectId: string
  onClose: () => void
}

export const ProjectDetailView: FC<ProjectDetailViewProps> = ({
  projectId,
  onClose
}) => {
  const { getProjectById } = useProjects()
  const { startChatInProjectWithMessage, loadSessionFromProjectView } = useChat()

  const [chats, setChats] = useState<ChatPreview[]>([])
  const [isLoadingChats, setIsLoadingChats] = useState(true)
  const [fileCount, setFileCount] = useState(0)
  const [showFilesDialog, setShowFilesDialog] = useState(false)

  const project = getProjectById(projectId)

  // Fetch chats for this project
  const fetchProjectChats = useCallback(async () => {
    setIsLoadingChats(true)
    try {
      const response = await api.get(`/api/projects/${projectId}/conversations`)
      const conversations = response.data.conversations || []
      setChats(conversations)
    } catch (err) {
      console.error('Failed to fetch project chats:', err)
      setChats([])
    } finally {
      setIsLoadingChats(false)
    }
  }, [projectId])

  // Fetch file count
  const fetchFileCount = useCallback(async () => {
    try {
      const files = await listProjectFiles(projectId)
      setFileCount(files.length)
    } catch (err) {
      console.error('Failed to fetch file count:', err)
    }
  }, [projectId])

  // Initial fetch on mount
  useEffect(() => {
    fetchProjectChats()
    fetchFileCount()
  }, [fetchProjectChats, fetchFileCount])

  // Re-fetch file count when dialog closes
  const handleFilesDialogChange = (open: boolean) => {
    setShowFilesDialog(open)
    if (!open) {
      // Re-fetch file count after closing
      fetchFileCount()
    }
  }

  const handleStartChatWithMessage = (message: string) => {
    startChatInProjectWithMessage(projectId, message)
    // onClose() not needed - startChatInProjectWithMessage sets viewMode to 'chat'
  }

  const handleChatSelect = (chatId: string) => {
    loadSessionFromProjectView(chatId)
  }

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0) return 'Today'
    if (days === 1) return 'Yesterday'
    if (days < 7) return date.toLocaleDateString('en-US', { weekday: 'short' })
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  if (!project) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Project not found</p>
          <Button variant="link" onClick={onClose} className="mt-2">
            Go back
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-none border-b border-border/50 px-4 py-4">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-8 w-8 rounded-lg shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <FolderOpen className="h-5 w-5 text-muted-foreground shrink-0" />
            <h1 className="text-lg font-semibold truncate">{project.name}</h1>
          </div>
          {/* Clickable file badge */}
          <button
            onClick={() => setShowFilesDialog(true)}
            className={cn(
              "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm",
              "bg-surface/50 hover:bg-surface border border-border/50",
              "transition-colors"
            )}
          >
            <FileIcon className="h-4 w-4" />
            <span>{fileCount} {fileCount === 1 ? 'file' : 'files'}</span>
          </button>
        </div>
      </div>

      {/* Files Dialog */}
      <Dialog open={showFilesDialog} onOpenChange={handleFilesDialogChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Project Files</DialogTitle>
          </DialogHeader>
          <ProjectFilesPanel projectId={projectId} />
        </DialogContent>
      </Dialog>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6">
          {/* Chat Input */}
          <ProjectChatInput
            projectName={project.name}
            onSend={handleStartChatWithMessage}
          />

          {/* Chat History */}
          {isLoadingChats ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : chats.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              No chats yet. Start typing above to begin.
            </div>
          ) : (
            <div className="mt-6 divide-y divide-border/50">
              {chats.map(chat => (
                <button
                  key={chat.id}
                  onClick={() => handleChatSelect(chat.id)}
                  className="w-full text-left py-4 hover:bg-accent/30 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{chat.title}</p>
                      {chat.lastMessage && (
                        <p className="text-sm text-muted-foreground truncate mt-0.5">
                          {chat.lastMessage}
                        </p>
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(chat.updatedAt)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
